import React, { useCallback, useMemo, useRef, useState } from 'react';
import CalendarView from './CalendarView';
import { todayWall } from './calendarTime';
import {
  VIEWS, addMonthsFirst, clampOffset, createChoicesFor, groupOptions, monthOffset, optionLabel, paneColors,
  rightStartDate, visiblePanes,
} from './calendarPanes';

// =============================================================================
// 5.33.0 Calendar "Side by side": two panes (left / right), each ONE calendar chosen from the pane options the
// server allows this login (GET /api/calendar/panes — same rules as everywhere; admins also get other people's
// Personal). Left '' = one pane only (logins without a personal calendar start so). A write in either pane bumps
// reloadKey so both panes refetch. Narrow windows stack the panes (calendar.css).
// 5.36.0 🔗 Link: LINKED = the 5.33 behaviour (one shared toolbar drives both panes: same view, same dates);
// UNLINKED (default) = each pane has its own small toolbar (prev / next / today, view, title) and moves alone. The
// same calendar may be in both panes. "Next month on the right" puts the right pane in Month on the left pane's month
// + 1 (unlinking first). Remembered per login (via onNavPrefs → user_settings): link, each pane's view and the MONTH
// OFFSET between the panes — never dates: on open the left pane is on today's month (or the date CalendarApp
// carries over) and the right pane on that month + offset.
// =============================================================================

const C = { panel: '#242424', border: '#333', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed', bg: '#1a1a1a' };
const NO_HIDDEN = new Set();
const sel = { padding: '4px 6px', background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, maxWidth: '100%' };
const navBtn = (active) => ({ padding: '4px 10px', border: `1px solid ${active ? C.violet : '#444'}`, borderRadius: 5, background: active ? C.violet : '#2f2f2f', color: '#f0f0f0', fontSize: 12, cursor: 'pointer' });
const today = () => new Date(`${todayWall()}T00:00:00Z`);

export function PanePicker({ options, value, onChange, allowNone, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={sel} data-testid={testid}>
      {allowNone && <option value="">— One pane only —</option>}
      {groupOptions(options).map(([g, list]) => (
        <optgroup key={g} label={g}>
          {list.map((o) => <option key={o.key} value={o.key}>{optionLabel(o)}{o.calendar.writable ? '' : ' (read-only)'}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// prev / next / today and the view buttons; `id` prefixes the test ids ("cal" = shared toolbar, "cal-left" / "cal-right").
function NavButtons({ id, onPrev, onNext, onToday }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <button type="button" style={navBtn(false)} onClick={onPrev} title="Previous" data-testid={`${id}-prev`}>‹</button>
      <button type="button" style={navBtn(false)} onClick={onNext} title="Next" data-testid={`${id}-next`}>›</button>
      <button type="button" style={navBtn(false)} onClick={onToday} data-testid={`${id}-today`}>Today</button>
    </div>
  );
}
function ViewButtons({ id, view, onView }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {VIEWS.map(([v, label]) => (
        <button key={v} type="button" style={navBtn(view === v)} onClick={() => onView(v)} data-testid={`${id}-view-${v}`}>{label}</button>
      ))}
    </div>
  );
}

export default function CalendarSideBySide({ options, left, right, onPick, me, nav, onNav, reloadKey, onChanged, openRequest,
  link = true, onLink, paneViews, offset = 0, onNavPrefs, showAll }) {
  const apis = useRef({});
  const colors = useMemo(() => paneColors(options), [options]);
  const panes = visiblePanes(options, left, right);
  const single = panes.length === 1;
  const slots = single ? ['right'] : ['left', 'right'];
  const linked = single || link;
  const driver = slots[0];                          // the pane whose date CalendarApp keeps (Overlay, remounts)
  // 5.34.0 a Today-panel click opens the event in the first pane of the login's own view (any own-view pane can
  // open an own Personal event: the server checks the view, not the pane's calendar)
  const openSlot = slots[panes.findIndex((o) => !o.user)];

  // Where each pane opens, fixed at mount: left = the carried date (today if none); right = left + offset when
  // unlinked. A pane remounted because its calendar changed reopens where it was (cur).
  const [start] = useState(() => {
    const l = { view: nav.view, date: nav.date || today() };
    return { left: l, right: (single || link) ? l : { view: (paneViews && paneViews.right) || nav.view, date: rightStartDate(l.date, offset) } };
  });
  const cur = useRef({});                           // slot -> {view, date} as last shown
  const inits = useRef({});                         // `${slot}:${key}` -> initial {view, date} of that mount
  const initFor = (slot, key) => {
    const k = `${slot}:${key}`;
    if (!inits.current[k]) {
      const other = cur.current[slot === 'left' ? 'right' : 'left'];
      inits.current[k] = cur.current[slot] || other || (single ? start.left : start[slot]);
    }
    return inits.current[k];
  };
  const [titles, setTitles] = useState({});
  const [views, setViews] = useState({});

  // the latest props for datesSet (one stable callback per slot)
  const latest = useRef({});
  latest.current = { linked, single, driver, paneViews, offset, onNav, onNavPrefs };

  const datesSet = useCallback((slot, arg) => {
    const L = latest.current;
    const view = arg.view.type;
    const date = arg.view.calendar.getDate();
    cur.current[slot] = { view, date };
    setTitles((t) => (t[slot] === arg.view.title ? t : { ...t, [slot]: arg.view.title }));
    setViews((v) => (v[slot] === view ? v : { ...v, [slot]: view }));
    if (slot === L.driver) L.onNav({ view, date });
    if (!L.onNavPrefs) return;
    const pv = L.paneViews || {};
    const save = {};
    if (L.linked) {
      if (slot === L.driver && (pv.left !== view || (!L.single && pv.right !== view))) {
        save.left_view = view;
        if (!L.single) save.right_view = view;
      }
    } else {
      if (pv[slot] !== view) save[`${slot}_view`] = view;
      const lc = cur.current.left;
      const rc = cur.current.right;
      if (lc && rc) {
        const off = clampOffset(monthOffset(lc.date, rc.date));
        if (off !== L.offset) save.offset = off;
      }
    }
    if (Object.keys(save).length) L.onNavPrefs(save);
  }, []);
  const setsFor = useMemo(() => ({ left: (a) => datesSet('left', a), right: (a) => datesSet('right', a) }), [datesSet]);

  const all = (fn) => slots.forEach((s) => { const a = apis.current[s]; if (a) fn(a); });
  const one = (slot, fn) => { const a = apis.current[slot]; if (a) fn(a); };

  const setLink = (on) => {
    if (on) {                                       // linking: the right pane joins the left pane's view + date
      const lc = cur.current.left;
      if (lc) one('right', (a) => a.changeView(lc.view, lc.date));
    }
    onLink(on);
  };
  const nextMonthRight = () => {
    const lc = cur.current.left;
    if (!lc) return;
    if (link) {
      onLink(false);
      latest.current = { ...latest.current, linked: false };   // datesSet below may fire before the re-render
    }
    one('right', (a) => a.changeView('dayGridMonth', addMonthsFirst(lc.date, 1)));
  };

  if (!panes.length) return <div style={{ color: C.dim, fontSize: 13 }}>No calendars yet — they appear within a minute of the first sync.</div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }} data-testid="cal-side" data-linked={linked ? '1' : '0'}>
      <div className="idcal-sharedbar">
        {linked && <NavButtons id="cal" onPrev={() => all((a) => a.prev())} onNext={() => all((a) => a.next())} onToday={() => all((a) => a.today())} />}
        {linked
          ? <div className="idcal-title idcal-title-shared" data-testid="cal-title">{titles[driver] || ''}</div>
          : <div style={{ flex: 1 }} />}
        {linked && <ViewButtons id="cal" view={views[driver] || nav.view} onView={(v) => all((a) => a.changeView(v))} />}
        {!single && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" style={navBtn(link)} onClick={() => setLink(!link)} data-testid="cal-link" aria-pressed={link}
              title={link ? 'Linked: one toolbar moves both panes. Click to give each pane its own toolbar.' : 'Not linked: each pane moves on its own. Click to move both panes together.'}>
              🔗 {link ? 'Linked' : 'Link'}
            </button>
            <button type="button" style={navBtn(false)} onClick={nextMonthRight} data-testid="cal-next-month-right"
              title="Right pane: Month view, the month after the left pane's">Next month on the right ›</button>
          </div>
        )}
      </div>
      <div className={`idcal-panes${single ? ' idcal-panes-single' : ''}`}>
        {panes.map((o, i) => {
          const slot = slots[i];
          const choices = createChoicesFor(o, options);
          const ov = { me, calendars: [o.calendar, ...choices.filter((c) => c.id !== o.calendar.id)] };
          const init = initFor(slot, o.key);
          return (
            <div key={slot} className="idcal-pane" data-testid={`cal-pane-${slot}`}>
              <div className="idcal-panehead" style={{ borderTop: `4px solid ${colors[o.key]}` }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 11, height: 11, borderRadius: 3, background: colors[o.key], flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: 13 }} data-testid={`cal-pane-${slot}-name`}>{optionLabel(o)}</span>
                  {!o.calendar.writable && <span style={{ fontSize: 11, color: C.dim }}>read-only</span>}
                  <div style={{ flex: 1 }} />
                  <PanePicker options={options} value={o.key} allowNone={slot === 'left'} testid={`cal-pick-${slot}`}
                    onChange={(k) => onPick(slot, k)} />
                  {single && (
                    <select value="" onChange={(e) => e.target.value && onPick('left', e.target.value)} style={sel} data-testid="cal-add-pane">
                      <option value="">＋ Second calendar…</option>
                      {groupOptions(options).map(([g, list]) => (
                        <optgroup key={g} label={g}>{list.map((x) => <option key={x.key} value={x.key}>{optionLabel(x)}</option>)}</optgroup>
                      ))}
                    </select>
                  )}
                </div>
                {!linked && (
                  <div className="idcal-panebar" data-testid={`cal-${slot}-bar`}>
                    <NavButtons id={`cal-${slot}`} onPrev={() => one(slot, (a) => a.prev())} onNext={() => one(slot, (a) => a.next())} onToday={() => one(slot, (a) => a.today())} />
                    <div className="idcal-title" data-testid={`cal-${slot}-title`}>{titles[slot] || ''}</div>
                    <ViewButtons id={`cal-${slot}`} view={views[slot] || init.view} onView={(v) => one(slot, (a) => a.changeView(v))} />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <CalendarView key={`${slot}:${o.key}`} target={o.user} overview={ov} colors={{ [o.calendar.id]: colors[o.key] }}
                  hidden={NO_HIDDEN} reloadKey={reloadKey} onChanged={onChanged}
                  calendarId={o.calendar.id} paneChoices={choices} showAll={showAll}
                  initialView={init.view} initialDate={init.date} onDatesSet={setsFor[slot]}
                  onApi={(a) => { apis.current[slot] = a; }}
                  openRequest={slot === openSlot ? openRequest : null} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
